import { useState, useEffect, useCallback } from 'react'
import { supabase } from '../../../shared/lib/supabase'
import { usePortalClient } from '../hooks/usePortalClient'
import { useRealtimeSubscription } from '../../../hooks/useRealtimeSubscription'
import { usePollingInterval } from '../../../hooks/usePollingInterval'

const CATEGORY_COLORS = {
  change: 'bg-blue-500',
  login: 'bg-green-500',
  system: 'bg-purple-500'
}

const PAGE_SIZE = 30

function PortalActivityLog() {
  const { clientId } = usePortalClient()
  const [logs, setLogs] = useState([])
  const [loading, setLoading] = useState(true)
  const [page, setPage] = useState(0)
  const [hasMore, setHasMore] = useState(true)
  const [newCount, setNewCount] = useState(0)

  const fetchLogs = useCallback(async () => {
    if (!clientId) return
    setLoading(true)

    const since = new Date(Date.now() - 72 * 60 * 60 * 1000).toISOString()
    const { data, count } = await supabase
      .from('audit_logs')
      .select('*', { count: 'exact' })
      .eq('client_id', clientId)
      .gte('created_at', since)
      .order('created_at', { ascending: false })
      .range(page * PAGE_SIZE, (page + 1) * PAGE_SIZE - 1)

    setLogs(data || [])
    setHasMore((page + 1) * PAGE_SIZE < (count || 0))
    setNewCount(0)
    setLoading(false)
  }, [clientId, page])

  useEffect(() => {
    fetchLogs()
  }, [fetchLogs])

  // Realtime: prepend new entries when on first page, otherwise show banner
  useRealtimeSubscription({
    table: 'audit_logs',
    filter: clientId ? `client_id=eq.${clientId}` : undefined,
    enabled: !!clientId,
    onInsert: (record) => {
      if (page === 0) {
        setLogs(prev => {
          if (prev.some(l => l.id === record.id)) return prev
          return [record, ...prev]
        })
      } else {
        setNewCount(c => c + 1)
      }
    }
  })

  // 5-minute polling fallback
  usePollingInterval(fetchLogs, 5 * 60 * 1000, !!clientId)

  const relativeTime = (dateStr) => {
    const now = new Date()
    const date = new Date(dateStr)
    const diffMs = now - date
    const diffMin = Math.floor(diffMs / 60000)
    const diffHr = Math.floor(diffMs / 3600000)
    const diffDay = Math.floor(diffMs / 86400000)

    if (diffMin < 1) return 'just now'
    if (diffMin < 60) return `${diffMin}m ago`
    if (diffHr < 24) return `${diffHr}h ago`
    if (diffDay < 30) return `${diffDay}d ago`
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    )
  }

  return (
    <div>
      <h1 className="text-2xl font-bold text-primary-600 dark:text-primary-400 mb-2">Activity Log</h1>
      <p className="text-sm text-gray-500 dark:text-gray-400 mb-6">
        Recent changes and updates to your portal.
      </p>

      {/* New activity banner when on a later page */}
      {newCount > 0 && page > 0 && (
        <button
          onClick={() => { setPage(0); setNewCount(0) }}
          className="w-full mb-4 py-2 px-4 bg-primary-50 dark:bg-primary-900/20 text-primary-600 dark:text-primary-400 text-sm font-medium rounded-lg hover:bg-primary-100 dark:hover:bg-primary-900/30 transition-colors"
        >
          {newCount} new {newCount === 1 ? 'entry' : 'entries'} available. Click to refresh.
        </button>
      )}

      {logs.length === 0 ? (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-12 text-center">
          <svg className="w-12 h-12 text-gray-400 dark:text-gray-500 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <p className="text-gray-500 dark:text-gray-400">No activity recorded yet.</p>
        </div>
      ) : (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700">
          <div className="divide-y divide-gray-100 dark:divide-gray-700">
            {logs.map(log => (
              <div key={log.id} className="flex items-start gap-3 px-5 py-4">
                {/* Timeline dot */}
                <div className="pt-1 flex-shrink-0">
                  <div className={`w-2.5 h-2.5 rounded-full ${CATEGORY_COLORS[log.category] || 'bg-gray-400'}`} />
                </div>

                {/* Content */}
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-gray-900 dark:text-white">
                    {log.description}
                  </p>
                  <div className="flex items-center gap-3 mt-1">
                    <span className="text-xs text-gray-400 dark:text-gray-500">
                      {relativeTime(log.created_at)}
                    </span>
                    {log.user_email && (
                      <span className="text-xs text-gray-400 dark:text-gray-500">
                        by {log.user_email}
                      </span>
                    )}
                  </div>
                </div>

                {/* Action badge */}
                <span className="text-xs text-gray-400 dark:text-gray-500 flex-shrink-0">
                  {log.action}
                </span>
              </div>
            ))}
          </div>

          {/* Load more */}
          {hasMore && (
            <div className="border-t border-gray-100 dark:border-gray-700 p-3 text-center">
              <button
                onClick={() => setPage(p => p + 1)}
                className="text-sm text-primary-600 hover:text-primary-700 dark:text-primary-400 dark:hover:text-primary-300 font-medium"
              >
                Load more
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  )
}

export default PortalActivityLog
